import { useState, useEffect, useRef, useCallback } from 'react';

interface UseInfiniteScrollOptions {
  threshold?: number;
  rootMargin?: string;
}

interface UseInfiniteScrollReturn {
  isFetching: boolean;
  setIsFetching: (fetching: boolean) => void;
  sentinelRef: React.RefObject<HTMLDivElement>;
}

const useInfiniteScroll = (
  callback: () => Promise<void> | void,
  options: UseInfiniteScrollOptions = {},
): UseInfiniteScrollReturn => {
  const [isFetching, setIsFetching] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { threshold = 0.1, rootMargin = '100px' } = options;

  const fetchMoreData = useCallback(async () => {
    if (isFetching) return;
    setIsFetching(true);
    try {
      await callback();
    } finally {
      setIsFetching(false);
    }
  }, [callback, isFetching]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      console.log('❌ No sentinel element found');
      return;
    }

    console.log('✅ Setting up Intersection Observer for sentinel:', sentinel);

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        console.log('📊 Intersection event:', {
          isIntersecting: entry.isIntersecting,
          intersectionRatio: entry.intersectionRatio,
          isFetching: isFetching,
        });

        if (entry.isIntersecting && !isFetching) {
          console.log('🚀 Triggering fetchMoreData');
          fetchMoreData();
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(sentinel);
    console.log('👀 Observer attached with options:', { threshold, rootMargin });

    return () => {
      console.log('🧹 Cleaning up observer');
      observer.unobserve(sentinel);
      observer.disconnect();
    };
  }, [fetchMoreData, isFetching, threshold, rootMargin]);

  return { isFetching, setIsFetching, sentinelRef };
};

export default useInfiniteScroll;
